import type { ReactNode } from 'react';
import { formatCurrency } from '@packages/format';
import {
  getProjectDisplayReference,
  type Client,
  type ClientProject,
  type ClientSearchResult,
  type InvoiceListItem,
  type InvoiceSearchResult,
  type MatterSearchResult,
} from '@packages/types';
import type { CopyFn } from '@packages/copy';
import type { RecentKind } from '../../lib/recents';
import type { SearchPopoverItem } from '../ui/SearchPopover';
import { resolveInvoiceStatusPresentation } from '../invoice/InvoiceStatus';

/** What an invoice row shows on its right: the total and the status badge's label and colour. */
export type OmniboxInvoiceFigures = {
  amountLabel: string;
  statusLabel: string;
  statusVariant: ReturnType<typeof resolveInvoiceStatusPresentation>['variant'];
};
/** A row's kind: a record that can be a recent, or a "Go to" destination (never recorded as a recent). */
export type OmniboxRowKind = RecentKind | 'destination';
export type OmniboxRow = {
  kind: OmniboxRowKind;
  id: string;
  label: string;
  sublabel?: string;
  invoice?: OmniboxInvoiceFigures;
};
export type OmniboxGroup = { heading: string; rows: OmniboxRow[] };

export const SUBLABEL_SEPARATOR = ' · ';

/**
 * Option ids carry the kind so one flat list can hold clients, matters,
 * invoices and destinations. A destination's id is its catalogue key.
 */
export function omniboxItemId(kind: OmniboxRowKind, id: string): string {
  return `${kind}:${id}`;
}

export function parseOmniboxItemId(value: string): { kind: OmniboxRowKind; id: string } | null {
  const separator = value.indexOf(':');
  if (separator === -1) return null;
  const kind = value.slice(0, separator);
  const id = value.slice(separator + 1);
  if (id === '' || (kind !== 'client' && kind !== 'matter' && kind !== 'invoice' && kind !== 'destination')) return null;
  return { kind, id };
}

export function omniboxItemPath(kind: RecentKind, id: string): string {
  switch (kind) {
    case 'client':
      return `/clients/${id}`;
    case 'matter':
      return `/matters/${id}`;
    case 'invoice':
      return `/invoices/${id}`;
  }
}

/**
 * Flattens groups for `SearchPopover`: each row carries its group heading and,
 * optionally, an icon and trailing content (an invoice's amount and status).
 */
export function toPopoverItems(
  groups: OmniboxGroup[],
  renderIcon?: (kind: OmniboxRowKind) => ReactNode,
  renderTrailing?: (row: OmniboxRow) => ReactNode,
): SearchPopoverItem[] {
  return groups.flatMap((group) =>
    group.rows.map((row) => ({
      id: omniboxItemId(row.kind, row.id),
      label: row.label,
      sublabel: row.sublabel,
      group: group.heading,
      icon: renderIcon?.(row.kind),
      trailing: renderTrailing?.(row),
    })),
  );
}

function matterSublabel(parts: { reference: string; clientName: string; inactive: boolean }, copy: CopyFn): string {
  const segments = [parts.reference, parts.clientName];
  if (parts.inactive) segments.push(copy('omnibox:inactive'));
  return segments.join(SUBLABEL_SEPARATOR);
}

function invoiceRow(
  invoice: {
    id: string;
    invoiceNumber: string | null;
    reference: string | null;
    clientName: string | null;
    totalMinorUnits: number;
    currency: string;
    status: InvoiceListItem['status'];
    paymentStatus?: InvoiceListItem['paymentStatus'];
    settledAmountMinorUnits?: number;
    creditedAmountMinorUnits?: number;
    writtenOffAmountMinorUnits?: number;
    liveProforma?: InvoiceListItem['liveProforma'];
    dueDate?: string | null;
    date?: string;
  },
  copy: CopyFn,
): OmniboxRow {
  const { label: statusLabel, variant } = resolveInvoiceStatusPresentation(invoice, copy);
  return {
    kind: 'invoice',
    id: invoice.id,
    label: invoice.invoiceNumber ?? invoice.reference ?? copy('omnibox:draftInvoice'),
    sublabel: invoice.clientName || undefined,
    invoice: {
      amountLabel: formatCurrency(invoice.totalMinorUnits, invoice.currency),
      statusLabel,
      statusVariant: variant,
    },
  };
}

export function clientRowFromSearch(hit: ClientSearchResult): OmniboxRow {
  return { kind: 'client', id: hit.id, label: hit.name };
}

export function matterRowFromSearch(hit: MatterSearchResult, copy: CopyFn): OmniboxRow {
  return {
    kind: 'matter',
    id: hit.id,
    label: hit.name,
    sublabel: matterSublabel(
      { reference: hit.displayReference, clientName: hit.client.name, inactive: hit.status === 'INACTIVE' },
      copy,
    ),
  };
}

export function invoiceRowFromSearch(hit: InvoiceSearchResult, copy: CopyFn): OmniboxRow {
  return invoiceRow({ ...hit, clientName: hit.client.name }, copy);
}

export function clientRowFromList(client: Client): OmniboxRow {
  return { kind: 'client', id: client.id, label: client.name };
}

export function matterRowFromList(project: ClientProject, copy: CopyFn): OmniboxRow {
  return {
    kind: 'matter',
    id: project.id,
    label: project.name,
    sublabel: matterSublabel(
      {
        reference: getProjectDisplayReference(project),
        clientName: project.clientName,
        inactive: project.status === 'INACTIVE',
      },
      copy,
    ),
  };
}

export function invoiceRowFromList(invoice: InvoiceListItem, copy: CopyFn): OmniboxRow {
  return invoiceRow(invoice, copy);
}
